import React, { useEffect, useState } from 'react'

export interface Step {
  title: string
  content: (currentStep?, setCurrentStep?) => React.ReactNode | string
  buttonLabel?: string
  disabledButton?: boolean
  loading?: boolean
  disabledStep?: boolean
  customClick?: any
  hideButton?: boolean
  stepColor?: string
}

interface FinishButton {
  label: string
  onClick: (currentStep?, setCurrentStep?) => void | Promise<void>
  extraDisabled?: boolean
  extraEnabled?: (currentStep) => boolean
  footerMessage?: string | React.ReactNode
  loadingFooter?: boolean
  buttonColor?: string
}

const Stepper = ({
  steps,
  finishButton,
  secondaryFinishButton,
  initialStep = 0,
}: {
  steps: Step[]
  finishButton: FinishButton
  initialStep?: number
  secondaryFinishButton?: FinishButton
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep)

  useEffect(() => {
    setCurrentStep(initialStep)
  }, [initialStep])

  return (
    <>
      <div className='flex flex-col justify-between px-4 pb-8'>
        {steps.map((step, index) => (
          <div
            key={index}
            className={`collapse rounded-none overflow-visible ${
              currentStep === index ?? 'collapse-open opacity-100'
            } relative border-neutral pl-5 border-l-[.5rem] pb-4 ${
              index === steps.length - 1 ? 'border-transparent' : ''
            }`}
          >
            <input
              type='checkbox'
              className='w-full min-h-10'
              checked={index === currentStep}
              onClick={() => setCurrentStep(index)}
              disabled={step.disabledStep}
              readOnly // Prevents error from using uncontrolled input
            />
            <div className='flex gap-2 collapse-title min-h-10 p-0 t-1 font-semibold'>
              <div
                className={`flex items-center justify-center w-8 h-8 ${
                  step.stepColor ? step.stepColor : 'bg-accent'
                } rounded-full text-white text-center left-[-40px] top-[-5px] absolute transition-all`}
              >
                <p>{index + 1}</p>
              </div>
              <div>{step.title}</div>
            </div>
            <div
              className={`collapse-content p-0 opacity-0 ${
                currentStep === index ? '!opacity-100 !transition-all !duration-500' : ''
              }`}
            >
              <div className='flex flex-row'>
                <div className='w-[80%]'>{step.content(currentStep, setCurrentStep)}</div>
                <div className='w-[20%] flex justify-end items-end'>
                  {!step.hideButton ? (
                    <button
                      type='button'
                      onClick={() => {
                        if (step.customClick) step.customClick(currentStep, setCurrentStep)
                        else setCurrentStep((currentStep) => currentStep + 1)
                      }}
                      className='btn btn-primary relative'
                      data-test='manuallycreatemaxdeal-next-step'
                      disabled={step.disabledButton}
                    >
                      {step.buttonLabel}
                      {step.loading && (
                        <div className='w-full h-full absolute flex justify-center items-center'>
                          <span className='loading loading-spinner loading-md text-black'></span>
                        </div>
                      )}
                    </button>
                  ) : (
                    <>
                      {step.loading && (
                        <div className='w-full h-full flex justify-center items-center'>
                          <span className='loading loading-spinner loading-md text-black'></span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div
        className={`fixed grid bg-white bottom-0 pb-3 pt-3 pr-[3.1rem] pl-[3.1rem] right-0 w-[576px] items-center ${
          secondaryFinishButton ? 'grid-cols-3' : 'grid-cols-2'
        } gap-3`}
        style={{ boxShadow: 'rgb(204 204 204 / 75%) 0px 4px 18px' }}
      >
        <div className='flex flex-row gap-3 items-center'>
          {finishButton.footerMessage && <div>{finishButton.footerMessage} </div>}
          {finishButton.loadingFooter && (
            <div className='flex justify-center items-center'>
              <span className='loading loading-spinner loading-md text-black'></span>
            </div>
          )}
        </div>

        {secondaryFinishButton && (
          <button
            type='button'
            className={`btn btn-primary relative justify-self-end ${secondaryFinishButton.buttonColor}`}
            onClick={() => secondaryFinishButton.onClick(currentStep, setCurrentStep)}
            disabled={
              (currentStep !== steps.length - 1 || secondaryFinishButton.extraDisabled) &&
              (secondaryFinishButton.extraEnabled
                ? !secondaryFinishButton.extraEnabled(currentStep)
                : true)
            }
          >
            {secondaryFinishButton.label}
          </button>
        )}
        <button
          type='button'
          className={`btn btn-primary relative justify-self-end ${finishButton.buttonColor}`}
          data-test='manuallycreatemaxdeal-createnewdeal-btn'
          onClick={() => finishButton.onClick(currentStep, setCurrentStep)}
          disabled={
            (currentStep !== steps.length - 1 || finishButton.extraDisabled) &&
            (finishButton.extraEnabled ? !finishButton.extraEnabled(currentStep) : true)
          }
        >
          {finishButton.label}
        </button>
      </div>
    </>
  )
}

export default Stepper
